import { DatePicker, Typography } from 'antd'
import dayjs from 'dayjs'
import { CorrosionRatePickerModal } from 'features/indicationTriage/cards/ffsCalc/corrosionRatePicker/CorrosionRatePickerModal'
import { useComponentInspectionsData } from 'features/indicationTriage/cards/ffsCalc/corrosionRatePicker/useComponentInspectionsData'
import {
  LABEL_WIDTH,
  SectionContainer,
  Sub,
} from 'features/indicationTriage/cards/ffsCalc/FfsCalcLayout'
import { FfsParameterField } from 'features/indicationTriage/cards/ffsCalc/FfsParameterField'
import { FfsSharedValuesContext } from 'features/indicationTriage/cards/ffsCalc/FfsSharedValues'
import { useIndicationData } from 'features/indicationTriage/IndicationContext'
import React, {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import styled from 'styled-components'
import { GeckoMeasurement, GeckoMeasurementUnit } from 'types'

const { Text } = Typography

const DateFieldContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: left;
  width: 100%;
  align-items: center;
  gap: 1rem;
`

export const LABEL_FCA_ML = (
  <>
    FCA<Sub>ml</Sub>
  </>
)

const RawFfsUserInputSection: FC<object> = () => {
  const {
    fca,
    setFca,
    fcaMl,
    setFcaMl,
    corrosionRate,
    setCorrosionRate,
    corrosionRateMl,
    setCorrosionRateMl,
    targetDate,
    setTargetDate,
    inspectionDate,
    setInspectionDate,
    designTemp,
    setDesignTemp,
    designPressure,
    setDesignPressure,
  } = useContext(FfsSharedValuesContext)

  // Get inspections data from indication context
  const {
    component,
    inspections: allInspections,
    componentDataFrames,
  } = useIndicationData()

  // Filter inspections and CDFs for this component
  const { inspections, inspectionCdfs } = useComponentInspectionsData(
    component?.id,
    allInspections,
    componentDataFrames
  )

  // State for the corrosion rate picker modal
  const [isCorrosionRatePickerOpen, setIsCorrosionRatePickerOpen] =
    useState(false)
  const [isCorrosionRateMlPickerOpen, setIsCorrosionRateMlPickerOpen] =
    useState(false)

  // Handler for confirming corrosion rate from picker
  const handleCorrosionRateConfirm = useCallback(
    (rate: GeckoMeasurement) => {
      setCorrosionRate(rate)
    },
    [setCorrosionRate]
  )

  // Handler for confirming corrosion rate ML from picker
  const handleCorrosionRateMlConfirm = useCallback(
    (rate: GeckoMeasurement) => {
      setCorrosionRateMl(rate)
    },
    [setCorrosionRateMl]
  )

  const yearsDifference: number | null = useMemo(() => {
    if (!targetDate || !inspectionDate) return null
    const target = dayjs(targetDate)
    const inspection = dayjs(inspectionDate)
    if (!target.isValid() || !inspection.isValid()) return null
    return target.diff(inspection, 'year', true)
  }, [targetDate, inspectionDate])

  useEffect(() => {
    if (corrosionRate && yearsDifference !== null) {
      const calculatedFca = GeckoMeasurement.CreateFromValue(
        corrosionRate.num * yearsDifference,
        GeckoMeasurementUnit.Millimeters
      )
      setFca(calculatedFca)
    }
  }, [corrosionRate, yearsDifference, setFca])

  useEffect(() => {
    if (corrosionRateMl && yearsDifference !== null) {
      const calculatedFcaMl = GeckoMeasurement.CreateFromValue(
        corrosionRateMl.num * yearsDifference,
        GeckoMeasurementUnit.Millimeters
      )
      setFcaMl(calculatedFcaMl)
    }
  }, [corrosionRateMl, yearsDifference, setFcaMl])

  const handleInspectionDateChange = useCallback(
    (date: dayjs.Dayjs | null) => {
      if (!date) {
        setInspectionDate(null)
        return
      }
      setInspectionDate(date.format('YYYY-MM-DD'))
    },
    [setInspectionDate]
  )

  const handleTargetDateChange = useCallback(
    (date: dayjs.Dayjs | null) => {
      if (!date) {
        setTargetDate(null)
        return
      }
      setTargetDate(date.format('YYYY-MM-DD'))
    },
    [setTargetDate]
  )

  return (
    <SectionContainer>
      <FfsParameterField
        label='Design Temp'
        labelTooltip='Design temperature for the component'
        labelWidth={LABEL_WIDTH}
        value={designTemp}
        setValue={setDesignTemp}
        measurementType='temperature'
        min={GeckoMeasurement.CreateFromValue(0, GeckoMeasurementUnit.Celsius)}
        step={5}
      />
      <FfsParameterField
        label='Design Press'
        labelTooltip='Design pressure (MAWP) for the component'
        labelWidth={LABEL_WIDTH}
        value={designPressure}
        setValue={setDesignPressure}
        measurementType='pressure'
        min={GeckoMeasurement.CreateFromValue(
          0,
          GeckoMeasurementUnit.Megapascals
        )}
        step={1}
      />
      <DateFieldContainer>
        <Text style={{ width: LABEL_WIDTH }}>Inspection Date</Text>
        <DatePicker
          value={inspectionDate ? dayjs(inspectionDate) : null}
          onChange={handleInspectionDateChange}
          format='YYYY-MM-DD'
          disabled
          style={{ width: '70%' }}
        />
      </DateFieldContainer>
      <DateFieldContainer>
        <Text style={{ width: LABEL_WIDTH }}>Target Date</Text>
        <DatePicker
          value={targetDate ? dayjs(targetDate) : null}
          onChange={handleTargetDateChange}
          format='YYYY-MM-DD'
          style={{ width: '70%' }}
        />
      </DateFieldContainer>
      {yearsDifference !== null && (
        <DateFieldContainer>
          <Text style={{ width: LABEL_WIDTH }}>Time Difference</Text>
          <Text style={{ width: '70%' }}>
            {yearsDifference.toFixed(3)} years
          </Text>
        </DateFieldContainer>
      )}
      <FfsParameterField
        label='CR'
        labelTooltip='Corrosion Rate - click table icon to pick from inspection data'
        labelWidth={LABEL_WIDTH}
        value={corrosionRate}
        setValue={setCorrosionRate}
        measurementType='rate'
        min={GeckoMeasurement.CreateFromValue(
          0,
          GeckoMeasurementUnit.MillimetersPerYear
        )}
        step={0.1}
        onLookup={() => setIsCorrosionRatePickerOpen(true)}
      />
      <FfsParameterField
        label={
          <>
            CR<Sub>ml</Sub>
          </>
        }
        labelTooltip='Corrosion Rate in region of metal loss - click table icon to pick from inspection data'
        labelWidth={LABEL_WIDTH}
        value={corrosionRateMl}
        setValue={setCorrosionRateMl}
        measurementType='rate'
        min={GeckoMeasurement.CreateFromValue(
          0,
          GeckoMeasurementUnit.MillimetersPerYear
        )}
        step={0.1}
        onLookup={() => setIsCorrosionRateMlPickerOpen(true)}
      />
      <FfsParameterField
        label='FCA'
        labelTooltip='Future Corrosion Allowance (calculated from CR × time difference)'
        labelWidth={LABEL_WIDTH}
        value={fca}
        min={GeckoMeasurement.CreateFromValue(
          0,
          GeckoMeasurementUnit.Millimeters
        )}
        step={0.1}
      />
      <FfsParameterField
        label={LABEL_FCA_ML}
        labelTooltip='FCA in region of metal loss (calculated from CR_ml × time difference)'
        labelWidth={LABEL_WIDTH}
        value={fcaMl}
        min={GeckoMeasurement.CreateFromValue(
          0,
          GeckoMeasurementUnit.Millimeters
        )}
        step={0.1}
      />

      {/* Corrosion Rate Picker Modals */}
      <CorrosionRatePickerModal
        open={isCorrosionRatePickerOpen}
        onClose={() => setIsCorrosionRatePickerOpen(false)}
        onConfirm={handleCorrosionRateConfirm}
        inspections={inspections}
        inspectionCdfs={inspectionCdfs}
        boundarySelectionEnabled={true}
      />
      <CorrosionRatePickerModal
        open={isCorrosionRateMlPickerOpen}
        onClose={() => setIsCorrosionRateMlPickerOpen(false)}
        onConfirm={handleCorrosionRateMlConfirm}
        inspections={inspections}
        inspectionCdfs={inspectionCdfs}
        boundarySelectionEnabled={false}
      />
    </SectionContainer>
  )
}

export const FfsUserInputSection = React.memo(RawFfsUserInputSection)
